import React, { useEffect, useRef, useState } from 'react';

import { Check, Layers } from 'lucide-react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { BASEMAP_PROVIDERS } from '@/components/SchedulePage/ScheduleMap/helpers/basemaps';
import classes from '@/components/SchedulePage/ScheduleMap/style.module.scss';

const ScheduleBasemapSelector = ({ activeBasemapId, onSelectBasemap }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const mapStyleTitle = intl.formatMessage({ id: 'SCHEDULE_MAP.BASEMAP.TITLE' });
  const mapStyleAriaLabel = intl.formatMessage({ id: 'SCHEDULE_MAP.BASEMAP.ARIA_LABEL' });
  const mapStyleHeader = intl.formatMessage({ id: 'SCHEDULE_MAP.BASEMAP.HEADER' });

  return (
    <div
      ref={containerRef}
      className={classes.basemapSelectorContainer}
      onMouseDown={e => e.stopPropagation()}
      onDoubleClick={e => e.stopPropagation()}
    >
      <button
        type="button"
        className={`${classes.basemapIconButton} ${isOpen ? classes.basemapIconButtonActive : ''}`}
        onClick={e => {
          e.stopPropagation();
          setIsOpen(prev => !prev);
        }}
        title={mapStyleTitle}
        aria-label={mapStyleAriaLabel}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <Layers className={classes.basemapIcon} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className={classes.basemapDropdown} role="menu" aria-orientation="vertical">
          <div className={classes.basemapDropdownHeader}>
            {mapStyleHeader}
          </div>
          <div className={classes.basemapOptionsList}>
            {BASEMAP_PROVIDERS.map(provider => {
              const isSelected = provider.id === activeBasemapId;
              const label = intl.formatMessage({ id: provider.labelKey });

              return (
                <button
                  key={provider.id}
                  type="button"
                  role="menuitem"
                  className={`${classes.basemapOptionItem} ${isSelected ? classes.basemapOptionSelected : ''}`}
                  onClick={e => {
                    e.stopPropagation();
                    onSelectBasemap(provider.id);
                    setIsOpen(false);
                  }}
                >
                  <span className={classes.basemapOptionName}>{label}</span>
                  {isSelected && <Check className={classes.basemapCheckIcon} aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

ScheduleBasemapSelector.propTypes = {
  activeBasemapId: PropTypes.string.isRequired,
  onSelectBasemap: PropTypes.func.isRequired,
};

export default React.memo(ScheduleBasemapSelector);
